import React, { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import LearningContentPage from './LearningContentPage';
import { MoodleNavigationProvider } from '../Moodle/MoodleNavigationContext';
import { MoodleUiProvider } from '../Moodle/MoodleUiContext';
import { MoodleUiConfig, MoodleViewState } from '../Moodle/types';

function getBackLabel(returnTo: string) {
  if (returnTo.includes('/journey-map/')) return 'Back to Journey Map';
  if (returnTo.includes('/workshops/train-the-trainer')) return 'Back to Train the Trainer';
  if (returnTo.includes('/workshops/event-summary')) return 'Back to Event Details';
  if (returnTo.includes('/workshops/')) return 'Back to Workshops';
  if (returnTo.startsWith('/leader')) return 'Back to Leader Dashboard';
  if (returnTo.includes('/enablement')) return 'Back to Enablement';
  return 'Exit back to toolbox';
}

export default function LearningCourseRoute() {
  const navigate = useNavigate();
  const { provider, instance, courseId } = useParams();
  const [searchParams] = useSearchParams();

  const normalizedProvider = provider ?? '';
  const normalizedInstance =
    instance === 'internal' ? 'internal' : instance === 'external' ? 'external' : '';
  const normalizedCourseId = courseId ?? '';

  const fullscreen = searchParams.get('fullscreen') === '1';
  const returnTo = searchParams.get('returnTo') || '/workshops/train-the-trainer';
  const itemId = searchParams.get('itemId');

  const parsedCourseId = Number(normalizedCourseId);
  const parsedItemId =
    itemId && Number.isFinite(Number(itemId)) ? Number(itemId) : undefined;

  const backLabel = getBackLabel(returnTo);

  const config: MoodleUiConfig = useMemo(
    () => ({
      instance:
        normalizedInstance === 'internal' ? 'internal' : 'external',
      mode: fullscreen ? 'fullscreen' : 'standalone',
      features: {
        showHeader: false,
        showCourseListTabs: false,
        showCategoryFilter: false,
        showEvents: false,
        showCreateEvent: false,
        defaultCourseTab: 'course-content',
        courseTabs: [
          { key: 'course-content', label: 'Course Content', enabled: true },
          { key: 'upcoming-events', label: 'Upcoming Events', enabled: false },
          { key: 'previous-events', label: 'Previous Events', enabled: false },
        ],
      },
    }),
    [normalizedInstance, fullscreen]
  );

  const initialView: MoodleViewState = useMemo(
    () => ({
      type: 'learning-content',
      courseId: Number.isFinite(parsedCourseId) ? parsedCourseId : 0,
      itemId: parsedItemId,
    }),
    [parsedCourseId, parsedItemId]
  );

  if (!normalizedProvider || !normalizedInstance || !normalizedCourseId || !Number.isFinite(parsedCourseId)) {
    return <div className="p-4">Invalid learning route.</div>;
  }

  if (normalizedProvider !== 'moodle') {
    return <div className="p-4">Unsupported learning provider: {normalizedProvider}</div>;
  }

  if (normalizedInstance !== 'external' && normalizedInstance !== 'internal') {
    return <div className="p-4">Unsupported learning instance: {normalizedInstance}</div>;
  }

  return (
    <MoodleUiProvider config={config}>
      <MoodleNavigationProvider
        initialView={initialView}
        goBack={() => navigate(returnTo)}
      >
        <LearningContentPage
          provider="moodle"
          courseId={String(parsedCourseId)}
          itemId={itemId ?? undefined}
          instance={normalizedInstance}
          fullscreen={fullscreen}
          returnTo={returnTo}
          onExit={() => navigate(returnTo)}
          backLabel={backLabel}
        />
      </MoodleNavigationProvider>
    </MoodleUiProvider>
  );
}